/**
 * EditTeamObjectiveDialog Component
 *
 * Dialog for editing team objective title and defined measure
 */

import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { okrApi } from '../../../services/okrApi';

export const EditTeamObjectiveDialog: React.FC = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { data, dialogs } = useOKRDashboardContext();
    const { setSuccess, setError, refetchData } = data;
    const {
        dialogs: dialogState,
        formState,
        updateFormField,
        closeEditTeamObjDialog,
    } = dialogs;

    const handleSubmit = async () => {
        if (!formState.editTeamObjId || !formState.editTeamObjTitle.trim()) return;

        try {
            await okrApi.updateTeamObjective(
                formState.editTeamObjId,
                formState.editTeamObjTitle.trim(),
                undefined,
                formState.editTeamObjMeasure?.trim() || undefined
            );
            setSuccess('Team objective updated successfully');
            closeEditTeamObjDialog();
            refetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to update team objective');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleSubmit();
        }
    };

    return (
        <Dialog
            open={dialogState.editTeamObjDialog}
            onClose={closeEditTeamObjDialog}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#fff',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    minHeight: '340px',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderBottom: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    pb: 2,
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Edit sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    Edit Team Objective
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pt: '24px !important', pb: 2, overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    <TextField
                        label="Objective Title"
                        value={formState.editTeamObjTitle}
                        onChange={(e) => updateFormField('editTeamObjTitle', e.target.value)}
                        onKeyDown={handleKeyDown}
                        fullWidth
                        required
                        autoFocus
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                            },
                        }}
                    />
                    <TextField
                        label="Defined Measure"
                        value={formState.editTeamObjMeasure}
                        onChange={(e) => updateFormField('editTeamObjMeasure', e.target.value)}
                        onKeyDown={handleKeyDown}
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="How will success be measured? (e.g., KPIs, metrics, deliverables)"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                            },
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                    >
                        Tip: Press Ctrl+Enter to save quickly
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                }}
            >
                <Button
                    onClick={closeEditTeamObjDialog}
                    sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!formState.editTeamObjTitle?.trim()}
                    sx={{
                        background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #0891b2 0%, #2563eb 100%)',
                        },
                    }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditTeamObjectiveDialog;
